import React from 'react';
import { Link } from 'react-router-dom';
import PageTitle from '../components/PageTitle';
import { 
  BookOpen, 
  Users, 
  Smartphone, 
  Shield, 
  Zap,
  CheckCircle,
  ArrowRight,
  Heart,
  Globe,
  Clock,
  Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const Sobre = () => {
  return (
    <>
      <PageTitle 
        title="Sobre o Projeto" 
        description="Conheça a plataforma de estudos bíblicos da Igreja de Jardim"
      />
      <div className="min-h-screen">
      {/* Hero Section */}
      <section className="igreja-hero text-white py-20 lg:py-32">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <Badge className="bg-white/20 text-white border-white/30 mb-4">
              Sobre o Projeto
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
              Estudos Bíblicos
              <span className="block text-church-green">Igreja de Jardim</span>
            </h1>
            <p className="text-xl lg:text-2xl text-white/90 leading-relaxed max-w-3xl mx-auto">
              Uma plataforma moderna e intuitiva para compartilhar, estudar e crescer 
              espiritualmente através da Palavra de Deus.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button size="lg" variant="secondary" asChild>
                <Link to="/register">
                  Começar Agora
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="ghost" 
                className="text-white border-2 border-white/70 hover:bg-white hover:text-church-blue bg-white/10 backdrop-blur-sm" 
                asChild
              >
                <Link to="#recursos">
                  Ver Recursos
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-church-blue mb-6">
                Nossa Missão
              </h2>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Facilitar o acesso e o estudo da Palavra de Deus através de uma plataforma 
                digital moderna, promovendo o crescimento espiritual e a edificação da comunidade cristã.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="text-center hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <div className="w-16 h-16 igreja-gradient rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Heart className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-church-blue">Edificação</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Promover o crescimento espiritual através de estudos bíblicos estruturados e reflexões profundas.
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <div className="w-16 h-16 igreja-gradient rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-church-blue">Comunidade</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Conectar pessoas através do estudo compartilhado da Palavra, fortalecendo os laços cristãos.
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <div className="w-16 h-16 igreja-gradient rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Globe className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-church-blue">Acessibilidade</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Tornar o estudo bíblico acessível a todos, em qualquer lugar e a qualquer momento.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="recursos" className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-church-blue mb-6">
                Recursos da Plataforma
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Desenvolvida com tecnologias modernas para oferecer a melhor experiência de estudo bíblico digital.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Card className="hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <BookOpen className="h-8 w-8 text-church-blue mb-2" />
                  <CardTitle>Editor Markdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Editor avançado com suporte completo ao Markdown para formatação rica dos estudos.
                  </p>
                  <div className="flex items-center text-green-600 text-sm">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Formatação rica e intuitiva
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <Smartphone className="h-8 w-8 text-church-blue mb-2" />
                  <CardTitle>Design Responsivo</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Interface otimizada para todos os dispositivos - desktop, tablet e smartphone.
                  </p>
                  <div className="flex items-center text-green-600 text-sm">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Acesso em qualquer dispositivo
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <Shield className="h-8 w-8 text-church-blue mb-2" />
                  <CardTitle>Sistema de Permissões</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Controle de acesso com diferentes níveis de permissão para administradores e autores.
                  </p>
                  <div className="flex items-center text-green-600 text-sm">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Segurança e organização
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <Zap className="h-8 w-8 text-church-blue mb-2" />
                  <CardTitle>Performance Otimizada</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Carregamento rápido e experiência fluida com tecnologias modernas como React 19.
                  </p>
                  <div className="flex items-center text-green-600 text-sm">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Experiência rápida e suave
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <Search className="h-8 w-8 text-church-blue mb-2" />
                  <CardTitle>Experiência de Leitura</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Typography otimizada com fontes premium para uma experiência de leitura excepcional.
                  </p>
                  <div className="flex items-center text-green-600 text-sm">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Leitura confortável e focada
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <Clock className="h-8 w-8 text-church-blue mb-2" />
                  <CardTitle>Temas Light/Dark</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Alternância entre temas claro e escuro para conforto visual em qualquer ambiente.
                  </p>
                  <div className="flex items-center text-green-600 text-sm">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Conforto visual personalizado
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-church-blue mb-6">
                Como Funciona
              </h2>
              <p className="text-xl text-muted-foreground">
                Processo simples e intuitivo para começar seus estudos bíblicos
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="w-16 h-16 bg-church-blue text-white rounded-full flex items-center justify-center text-2xl font-bold shrink-0">
                  1
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2">Cadastre-se na Plataforma</h3>
                  <p className="text-muted-foreground">
                    Crie sua conta gratuita em segundos e tenha acesso a todos os estudos bíblicos disponíveis.
                  </p>
                </div>
                <ArrowRight className="h-6 w-6 text-muted-foreground hidden md:block" />
              </div>

              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="w-16 h-16 bg-church-blue text-white rounded-full flex items-center justify-center text-2xl font-bold shrink-0">
                  2
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2">Explore os Estudos</h3>
                  <p className="text-muted-foreground">
                    Navegue pela biblioteca de estudos bíblicos organizados por temas e livros da Bíblia.
                  </p>
                </div>
                <ArrowRight className="h-6 w-6 text-muted-foreground hidden md:block" />
              </div>

              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="w-16 h-16 bg-church-green text-white rounded-full flex items-center justify-center text-2xl font-bold shrink-0">
                  3
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2">Estude e Cresça</h3>
                  <p className="text-muted-foreground">
                    Mergulhe nos estudos com nossa interface otimizada para leitura e reflexão espiritual.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 igreja-gradient text-white text-center">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl lg:text-4xl font-bold">
              Pronto para transformar seu estudo bíblico?
            </h2>
            <p className="text-xl text-white/90">
              Junte-se à nossa comunidade e descubra uma nova forma de estudar a Palavra de Deus.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button size="lg" variant="secondary" asChild>
                <Link to="/register">
                  Criar Conta Gratuita
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="ghost" 
                className="text-white border-2 border-white/70 hover:bg-white hover:text-church-blue bg-white/10 backdrop-blur-sm" 
                asChild
              >
                <Link to="/contato">
                  Entre em Contato
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Sobre;